import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { AuthCard } from '@sentinel/ui';
import { isSafeReturnTo } from '@sentinel/auth-core';
import { useConsumeMagicLink } from '../features/magic-link/use-consume';

const searchSchema = z.object({
  return_to: z.string().optional(),
});

type ViewState = { kind: 'pending' } | { kind: 'error' };

export function MagicLinkConsumePage() {
  const { t } = useTranslation('magic-link');
  const { token } = Route.useParams();
  const { return_to } = Route.useSearch();
  const consume = useConsumeMagicLink();
  const [view, setView] = useState<ViewState>({ kind: 'pending' });
  const fired = useRef(false);

  // Strip the token from history so a Back button / refresh / shared
  // history sync cannot replay the (now-consumed) credential.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.history.replaceState) return;
    const cleanPath = window.location.pathname.replace(/\/[^/]+$/, '/_consumed');
    window.history.replaceState(null, '', cleanPath + window.location.search);
  }, []);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    void (async () => {
      try {
        const outcome = await consume.mutateAsync(token);
        const safe = isSafeReturnTo(return_to) ? return_to : undefined;
        const rtQuery = safe ? `&return_to=${encodeURIComponent(safe)}` : '';
        if (outcome.kind === 'mfa_required') {
          window.location.assign(`/login?mfa=required${rtQuery}`);
          return;
        }
        if (outcome.kind === 'mfa_enrollment_required') {
          window.location.assign(`/login?mfa_enrollment_required=true${rtQuery}`);
          return;
        }
        window.location.assign(safe ?? '/launch');
      } catch {
        setView({ kind: 'error' });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (view.kind === 'pending') {
    return (
      <AuthCard title={t('consume_pending')}>
        <p role="status" aria-live="polite">{t('consume_pending')}</p>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t('consume_error_title')} subtitle={t('consume_error_message')}>
      <p>
        <Link to="/magic-link">{t('retry_button')}</Link>
      </p>
      <p style={{ textAlign: 'center' }}>
        <Link to="/login">{t('back_to_login')}</Link>
      </p>
    </AuthCard>
  );
}

export const Route = createFileRoute('/magic-link/$token')({
  validateSearch: searchSchema,
  component: MagicLinkConsumePage,
});
